import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Star, User, Shield, Clock, Heart, MessageSquare, ThumbsUp } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useSupabaseUser } from '@/hooks/use-supabase-user';
import { useAdminCustomization } from '@/hooks/useAdminCustomization';
import { useLanguage } from '@/contexts/LanguageContext';

interface ClientEvaluationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guardId?: string;
  guardName?: string;
  guardAvatar?: string;
  teamId?: string;
}

const evaluationSchema = z.object({
  professionalism_score: z.number().min(1).max(5),
  reliability_score: z.number().min(1).max(5),
  communication_score: z.number().min(1).max(5),
  quality_of_service_score: z.number().min(1).max(5),
  punctuality_score: z.number().min(1).max(5),
  company_representation_score: z.number().min(1).max(5),
  overall_satisfaction: z.number().min(1).max(5),
  positive_feedback: z.string().optional(),
  improvement_suggestions: z.string().optional(),
  additional_comments: z.string().optional(),
  would_recommend: z.boolean(),
});

type EvaluationFormData = z.infer<typeof evaluationSchema>;

const getTraits = (appName: string, t: any) => [
  {
    key: 'professionalism_score' as const,
    name: t('professionalism') || 'Επαγγελματισμός',
    description: t('professionalismDesc')?.replace('{appName}', appName) || `Επαγγελματική συμπεριφορά και εμφάνιση του προσωπικού της ${appName}`,
    icon: <User className="h-4 w-4" />,
    color: 'text-blue-600'
  },
  {
    key: 'reliability_score' as const,
    name: t('reliability') || 'Αξιοπιστία',
    description: t('reliabilityDesc') || 'Συνέπεια και υπευθυνότητα στα καθήκοντα ασφαλείας',
    icon: <Shield className="h-4 w-4" />,
    color: 'text-green-600'
  },
  {
    key: 'communication_score' as const,
    name: t('communication') || 'Επικοινωνία',
    description: t('communicationDesc') || 'Ικανότητα επικοινωνίας με το προσωπικό σας',
    icon: <MessageSquare className="h-4 w-4" />,
    color: 'text-purple-600'
  },
  {
    key: 'quality_of_service_score' as const,
    name: t('qualityOfService') || 'Ποιότητα Υπηρεσιών',
    description: t('qualityOfServiceDesc') || 'Επίπεδο παρεχόμενων υπηρεσιών',
    icon: <Star className="h-4 w-4" />,
    color: 'text-yellow-600'
  },
  {
    key: 'punctuality_score' as const,
    name: t('punctuality') || 'Συνέπεια στο Πρόγραμμα',
    description: t('punctualityDesc') || 'Έγκαιρη άφιξη και τήρηση ωραρίου',
    icon: <Clock className="h-4 w-4" />,
    color: 'text-indigo-600'
  },
  {
    key: 'company_representation_score' as const,
    name: t('companyRepresentation')?.replace('{appName}', appName) || `Εκπροσώπηση ${appName}`,
    description: t('companyRepresentationDesc')?.replace('{appName}', appName) || `Πώς το προσωπικό εκπροσωπεί την ${appName} στις εγκαταστάσεις σας`,
    icon: <Shield className="h-4 w-4" />,
    color: 'text-orange-600'
  },
  {
    key: 'overall_satisfaction' as const,
    name: t('overallSatisfaction') || 'Συνολική Ικανοποίηση',
    description: t('overallSatisfactionDesc')?.replace('{appName}', appName) || `Γενική ικανοποίηση από το προσωπικό ${appName}`,
    icon: <Heart className="h-4 w-4" />,
    color: 'text-red-600'
  },
];

const StarRating: React.FC<{
  value: number;
  onChange: (value: number) => void;
  name: string;
}> = ({ value, onChange, name }) => {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          className={`p-1 rounded transition-colors ${
            star <= value 
              ? 'text-yellow-500 hover:text-yellow-600' 
              : 'text-gray-300 hover:text-gray-400'
          }`}
        >
          <Star 
            className={`h-5 w-5 ${star <= value ? 'fill-current' : ''}`} 
          />
        </button>
      ))}
      <span className="ml-2 text-sm text-gray-600">
        {value}/5
      </span>
    </div>
  );
};

export const ClientEvaluationModal: React.FC<ClientEvaluationModalProps> = ({
  open,
  onOpenChange,
  guardId,
  guardName,
  guardAvatar,
  teamId,
}) => {
  const { user } = useSupabaseUser();
  const queryClient = useQueryClient();
  const { customization } = useAdminCustomization();
  const { t } = useLanguage();
  const [isEditing, setIsEditing] = React.useState(false);
  const [existingEvaluationId, setExistingEvaluationId] = React.useState<string | null>(null);
  
  // Get app name from branding or use default
  const appName = customization?.app_name || 'OVIT';
  const traits = getTraits(appName, t);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<EvaluationFormData>({
    resolver: zodResolver(evaluationSchema),
    defaultValues: {
      professionalism_score: 5,
      reliability_score: 5,
      communication_score: 5,
      quality_of_service_score: 5,
      punctuality_score: 5,
      company_representation_score: 5,
      overall_satisfaction: 5,
      would_recommend: true,
    },
  });

  // Check for existing evaluation when modal opens
  React.useEffect(() => {
    if (open && guardId && user?.id) {
      const checkExistingEvaluation = async () => {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentSemester = currentDate.getMonth() >= 6 ? 'H2' : 'H1';

        const { data: existingEvaluation } = await supabase
          .from('client_evaluations')
          .select('*')
          .eq('guard_id', guardId)
          .eq('client_id', user.id)
          .eq('evaluation_year', currentYear)
          .eq('evaluation_period', currentSemester)
          .maybeSingle();

        if (existingEvaluation) {
          setIsEditing(true);
          setExistingEvaluationId(existingEvaluation.id);
          // Pre-populate form with existing data
          setValue('professionalism_score', existingEvaluation.professionalism_score || 5);
          setValue('reliability_score', existingEvaluation.reliability_score || 5);
          setValue('communication_score', existingEvaluation.communication_score || 5);
          setValue('quality_of_service_score', existingEvaluation.quality_of_service_score || 5);
          setValue('punctuality_score', existingEvaluation.punctuality_score || 5);
          setValue('company_representation_score', existingEvaluation.company_representation_score || 5);
          setValue('overall_satisfaction', existingEvaluation.overall_satisfaction || 5);
          setValue('positive_feedback', existingEvaluation.positive_feedback || '');
          setValue('improvement_suggestions', existingEvaluation.improvement_suggestions || '');
          setValue('additional_comments', existingEvaluation.additional_comments || '');
          setValue('would_recommend', existingEvaluation.would_recommend || true);
        } else {
          setIsEditing(false);
          setExistingEvaluationId(null);
        }
      };

      checkExistingEvaluation();
    }
  }, [open, guardId, user?.id, setValue]);

  const createEvaluation = useMutation({
    mutationFn: async (data: EvaluationFormData) => {
      if (!user || !guardId || !teamId) {
        throw new Error('Missing required data');
      }

      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentSemester = currentDate.getMonth() >= 6 ? 'H2' : 'H1';

      const evaluationData = {
        ...data,
        guard_id: guardId,
        client_id: user.id,
        team_id: teamId,
        evaluation_period: currentSemester,
        evaluation_year: currentYear,
        submitted_at: new Date().toISOString(),
      };

      const { data: result, error } = await supabase
        .from('client_evaluations')
        .insert([evaluationData])
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      toast.success(t('evaluationSubmittedSuccess') || 'Η αξιολόγηση υποβλήθηκε επιτυχώς!');
      queryClient.invalidateQueries({ queryKey: ['client-assigned-guards'] });
      queryClient.invalidateQueries({ queryKey: ['performance-client-evaluations'] });
      queryClient.invalidateQueries({ queryKey: ['client-evaluations'] });
      queryClient.invalidateQueries({ queryKey: ['current-client-evaluation'] });
      queryClient.invalidateQueries({ queryKey: ['can-client-evaluate'] });
      reset();
      onOpenChange(false);
    },
    onError: (error: any) => {
      console.error('Error creating evaluation:', error);
      toast.error(t('errorSubmittingEvaluation') || 'Σφάλμα κατά την υποβολή της αξιολόγησης');
    },
  });

  const updateEvaluation = useMutation({
    mutationFn: async (data: EvaluationFormData) => {
      if (!existingEvaluationId) {
        throw new Error('No evaluation ID for update');
      }

      const updateData = {
        ...data,
        submitted_at: new Date().toISOString(),
      };

      const { data: result, error } = await supabase
        .from('client_evaluations')
        .update(updateData)
        .eq('id', existingEvaluationId)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      toast.success(t('evaluationUpdatedSuccess') || 'Η αξιολόγηση ενημερώθηκε επιτυχώς!');
      queryClient.invalidateQueries({ queryKey: ['client-assigned-guards'] });
      queryClient.invalidateQueries({ queryKey: ['performance-client-evaluations'] });
      queryClient.invalidateQueries({ queryKey: ['client-evaluations'] });
      queryClient.invalidateQueries({ queryKey: ['current-client-evaluation'] });
      queryClient.invalidateQueries({ queryKey: ['can-client-evaluate'] });
      reset();
      onOpenChange(false);
    },
    onError: (error: any) => {
      console.error('Error updating evaluation:', error);
      toast.error(t('errorUpdatingEvaluation') || 'Σφάλμα κατά την ενημέρωση της αξιολόγησης');
    },
  });

  const onSubmit = (data: EvaluationFormData) => {
    if (isEditing) {
      updateEvaluation.mutate(data);
    } else {
      createEvaluation.mutate(data);
    }
  };

  const watchedValues = watch();

  const averageScore = traits.reduce((sum, trait) => 
    sum + (watchedValues[trait.key] || 0), 0) / traits.length;

  const getScoreColor = (score: number) => {
    if (score >= 4.5) return 'text-green-600';
    if (score >= 3.5) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 4.5) return t('excellent') || 'Εξαιρετικό';
    if (score >= 3.5) return t('good') || 'Καλό';
    if (score >= 2.5) return t('average') || 'Μέτριο';
    return t('needsImprovement') || 'Χρειάζεται Βελτίωση';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-500" />
            {isEditing ? (t('editEvaluation') || 'Επεξεργασία Αξιολόγησης') : (t('evaluatePersonnel')?.replace('{appName}', appName) || `Αξιολόγηση Προσωπικού ${appName}`)}
          </DialogTitle>
          <DialogDescription className="space-y-4">
            {/* Guard Avatar and Info */}
            <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg border">
              <Avatar className="h-16 w-16 ring-2 ring-blue-500/20">
                <AvatarImage src={guardAvatar} alt={guardName} />
                <AvatarFallback className="text-lg font-semibold bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                  {guardName?.split(' ').map(n => n[0]).join('').toUpperCase() || 'FG'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="font-semibold text-xl text-foreground mb-1">{guardName}</h3>
                <p className="text-sm text-blue-600 font-medium mb-1">{t('personnelFrom')?.replace('{appName}', appName) || `Προσωπικό ${appName}`}</p>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-green-100 text-green-700 border-green-200">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                    {t('active') || 'Ενεργός'}
                  </Badge>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">{t('evaluatedBy') || 'Αξιολογείται από'}</div>
                <div className="text-sm font-medium text-foreground">{appName}</div>
              </div>
            </div>
            <div>
              <p>{t('evaluatePerformanceOf')?.replace('{guardName}', guardName || '').replace('{appName}', appName) || `Αξιολογήστε την απόδοση του προσωπικού ${guardName} από την ${appName}`}</p>
              <p className="text-sm text-muted-foreground">
                {t('evaluationHelps')?.replace('{appName}', appName) || `Η αξιολόγησή σας βοηθά την ${appName} να διατηρεί υψηλό επίπεδο υπηρεσιών στις εγκαταστάσεις σας`}
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Overall Score Display */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{t('overallScore') || 'Συνολική Βαθμολογία'}</span>
                <div className="text-right">
                  <div className={`text-2xl font-bold ${getScoreColor(averageScore)}`}>
                    {averageScore.toFixed(1)}/5
                  </div>
                  <div className={`text-sm ${getScoreColor(averageScore)}`}>
                    {getScoreLabel(averageScore)}
                  </div>
                </div>
              </CardTitle>
            </CardHeader>
          </Card>

          {/* Traits Evaluation */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {traits.map((trait) => (
              <Card key={trait.key}>
                <CardHeader className="pb-3">
                  <CardTitle className={`flex items-center gap-2 text-sm ${trait.color}`}>
                    {trait.icon}
                    {trait.name}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    {trait.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <StarRating
                    value={watchedValues[trait.key] || 0}
                    onChange={(value) => setValue(trait.key, value)}
                    name={trait.key}
                  />
                  {errors[trait.key] && (
                    <p className="text-red-500 text-xs mt-1">
                      {t('pleaseSelectRating') || 'Παρακαλώ επιλέξτε βαθμολογία'}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Recommendation */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm">
                <ThumbsUp className="h-4 w-4" />
                {t('recommendPersonnel')?.replace('{appName}', appName) || `Σύσταση για το Προσωπικό ${appName}`}
              </CardTitle>
              <CardDescription className="text-xs">
                {t('wouldRecommendPersonnel')?.replace('{appName}', appName) || `Θα συστήνατε αυτό το προσωπικό της ${appName} σε άλλες εταιρείες;`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    {...register('would_recommend')}
                    value="true"
                    className="text-green-600"
                  />
                  <span className="text-green-600">{t('yesRepresentsWell')?.replace('{appName}', appName) || `Ναι, εκπροσωπεί άξια την ${appName}`}</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    {...register('would_recommend')}
                    value="false"
                    className="text-red-600"
                  />
                  <span className="text-red-600">{t('noNeedsImprovement') || 'Όχι, χρειάζεται βελτίωση'}</span>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Comments Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm text-green-600">
                  {t('positiveCommentsFor')?.replace('{appName}', appName) || `Θετικά Σχόλια για την ${appName}`}
                </CardTitle>
                <CardDescription className="text-xs">
                  {t('whatImpressedYou')?.replace('{appName}', appName) || `Τι σας εντυπωσίασε θετικά στον φύλακα της ${appName};`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  {...register('positive_feedback')}
                  placeholder={t('positiveCommentsPlaceholder') || "π.χ. Άψογη εμφάνιση, ευγενική συμπεριφορά, υψηλό επίπεδο ασφαλείας..."}
                  className="min-h-[100px]"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm text-orange-600">
                  {t('improvementSuggestions') || 'Προτάσεις Βελτίωσης'}
                </CardTitle>
                <CardDescription className="text-xs">
                  {t('whereCouldImprove')?.replace('{appName}', appName) || `Σε ποια σημεία θα μπορούσε η ${appName} να βελτιώσει τις υπηρεσίες της;`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  {...register('improvement_suggestions')}
                  placeholder={t('improvementSuggestionsPlaceholder') || "π.χ. Περισσότερη εκπαίδευση σε πρωτόκολλα, καλύτερη επικοινωνία..."}
                  className="min-h-[100px]"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm text-blue-600">
                  {t('generalCommentsFor')?.replace('{appName}', appName) || `Γενικά Σχόλια για την ${appName}`}
                </CardTitle>
                <CardDescription className="text-xs">
                  {t('howDoYouRate')?.replace('{appName}', appName) || `Πώς αξιολογείτε τη συνολική συνεργασία με την ${appName};`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  {...register('additional_comments')}
                  placeholder={t('generalCommentsPlaceholder')?.replace('{appName}', appName) || `Η γενική εντύπωσή σας από τον φύλακα και την ${appName}...`}
                  className="min-h-[100px]"
                />
              </CardContent>
            </Card>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-6 border-t">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
            >
              {t('cancel') || 'Ακύρωση'}
            </Button>
            <Button 
              type="submit" 
              disabled={createEvaluation.isPending || updateEvaluation.isPending}
            >
              {(createEvaluation.isPending || updateEvaluation.isPending) 
                ? (t('saving') || 'Αποθήκευση...') 
                : isEditing 
                  ? (t('updateEvaluation') || 'Ενημέρωση Αξιολόγησης')
                  : (t('submitEvaluation') || 'Υποβολή Αξιολόγησης')
              }
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};